import { useEffect, useId, useRef, useState } from "react"
import { useParams } from "react-router"
import { useTranslation } from "react-i18next"
import type { FormDescriptionImage } from "@f2e/shared/types"
import { Button, Input, Label } from "@f2e/ui/components/base"
import { DescriptionImage } from "@f2e/ui/components/InteractionRequestForm"

import { FetchMethod, useCodeerApi } from "@/hooks/useCodeerApi"
import { getUserFacingApiErrorMessage } from "@/lib/apiErrors"
import type { FormField } from "@/types/requestForm"
import type { Response } from "@/types"

export interface DescriptionImageEditorProps {
  field: FormField
  onChange: (image: FormDescriptionImage | undefined) => void
  disabled?: boolean
  imageCount: number
  onUploadingChange: (uploading: boolean) => void
}

const key = "workspace.agent.basicForm.formBuilder.image"

export const DescriptionImageEditor = ({
  field,
  onChange,
  disabled,
  imageCount,
  onUploadingChange,
}: DescriptionImageEditorProps) => {
  const { t } = useTranslation()
  const { workspaceId } = useParams()
  const id = useId()
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState("")
  const callbacks = useRef({ onChange, onUploadingChange })
  callbacks.current = { onChange, onUploadingChange }
  const pendingUpload = useRef<AbortController | null>(null)
  const { fetchFn } = useCodeerApi<
    Response<Omit<FormDescriptionImage, "altText">>,
    FormData
  >("/form-requests/images", FetchMethod.Post)

  useEffect(
    () => () => {
      if (pendingUpload.current) {
        pendingUpload.current.abort()
        pendingUpload.current = null
        setUploading(false)
        callbacks.current.onUploadingChange(false)
      }
    },
    [workspaceId, field.id]
  )

  const upload = async (files: FileList | null) => {
    if (!files?.length || uploading || disabled) return
    const file = files[0]
    if (files.length !== 1) return setError(t(`${key}.oneOnly`))
    if (!["image/jpeg", "image/png"].includes(file.type))
      return setError(t(`${key}.formatError`))
    if (file.size === 0 || file.size > 2 * 1024 * 1024)
      return setError(t(`${key}.sizeError`))
    if (!field.descriptionImage && imageCount >= 10)
      return setError(t(`${key}.countError`))
    setError("")
    setUploading(true)
    callbacks.current.onUploadingChange(true)
    const body = new FormData()
    body.append("file", file)
    const controller = new AbortController()
    pendingUpload.current = controller
    try {
      await fetchFn({
        signal: controller.signal,
        searchParams: new URLSearchParams({ workspace_id: workspaceId || "" }),
        body,
        callbackFn: (response) => {
          if (controller.signal.aborted) return
          if (!response?.data?.attachmentId) {
            setError(t(`${key}.uploadError`))
            return
          }
          callbacks.current.onChange({
            ...response.data,
            // Whitespace-only text would immediately fail the alt-text check.
            altText:
              field.descriptionImage?.altText.trim() ||
              field.question?.trim() ||
              file.name,
          })
        },
      })
    } catch (cause) {
      if (!controller.signal.aborted)
        setError(
          getUserFacingApiErrorMessage(cause, t, t(`${key}.uploadError`))
        )
    } finally {
      if (!controller.signal.aborted) {
        pendingUpload.current = null
        setUploading(false)
        callbacks.current.onUploadingChange(false)
      }
    }
  }

  return (
    <div className="space-y-3 pt-3">
      <Label htmlFor={`${id}-file`}>{t(`${key}.label`)}</Label>
      <p className="text-xs text-muted-foreground">{t(`${key}.limits`)}</p>
      {field.descriptionImage && (
        <DescriptionImage image={field.descriptionImage} />
      )}
      <div className="flex items-center gap-2 flex-wrap">
        <Input
          id={`${id}-file`}
          aria-label={t(
            field.descriptionImage ? `${key}.replace` : `${key}.upload`
          )}
          type="file"
          accept="image/jpeg,image/png"
          disabled={disabled || uploading || !workspaceId}
          className="max-w-sm"
          onChange={(event) => {
            void upload(event.target.files)
            event.target.value = ""
          }}
        />
        {field.descriptionImage && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={disabled || uploading}
            onClick={() => {
              onChange(undefined)
              setError("")
            }}
          >
            {t(`${key}.remove`)}
          </Button>
        )}
      </div>
      {uploading && (
        <p role="status" className="text-xs text-muted-foreground">
          {t(`${key}.uploading`)}
        </p>
      )}
      {error && (
        <p role="alert" className="text-xs text-destructive">
          {error}
        </p>
      )}
      {field.descriptionImage && (
        <div className="space-y-1">
          <Label htmlFor={`${id}-alt`}>{t(`${key}.altText`)}</Label>
          <Input
            id={`${id}-alt`}
            value={field.descriptionImage.altText}
            required
            disabled={disabled || uploading}
            onChange={(event) =>
              onChange({
                ...field.descriptionImage!,
                altText: event.target.value,
              })
            }
          />
          {!field.descriptionImage.altText.trim() && (
            <p className="text-xs text-destructive">
              {t(`${key}.altRequired`)}
            </p>
          )}
        </div>
      )}
    </div>
  )
}
